import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useRules } from '../context/RuleContext';
import { validateLocationId } from '../utils/validator';

export default function LocationGroups() {
  const { locationGroups, addLocationGroup, updateLocationGroup, deleteLocationGroup } = useRules();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [tableNum, setTableNum] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [locations, setLocations] = useState([]);
  const [error, setError] = useState('');

  const handleOpenAdd = () => {
    setEditingGroup(null);
    setTableNum('');
    setLocations([]);
    setError('');
    setDialogOpen(true);
  };

  const handleOpenEdit = (group) => {
    setEditingGroup(group);
    setTableNum(group.tableNum);
    setLocations([...group.locations]);
    setError('');
    setDialogOpen(true);
  };

  const handleAddLocation = () => {
    const val = locationInput.trim().toUpperCase();
    const err = validateLocationId(val);
    if (err) { setError(err); return; }
    if (locations.includes(val)) { setError('Already added'); return; }
    setLocations([...locations, val]);
    setLocationInput('');
    setError('');
  };

  const handleSubmit = () => {
    if (!tableNum) { setError('Table number required'); return; }
    if (locations.length === 0) { setError('At least one location required'); return; }

    if (editingGroup) {
      updateLocationGroup(editingGroup.tableNum, locations);
    } else {
      addLocationGroup({ tableNum, locations });
    }
    setDialogOpen(false);
  };

  const handleDelete = (tNum) => {
    if (window.confirm(`Delete location group #${tNum}?`)) deleteLocationGroup(tNum);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Location Groups ({locationGroups.length})</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>Add Location Group</Button>
      </Box>

      {locationGroups.length === 0 ? (
        <Card><CardContent sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary">No location groups. Upload an XML file or add one.</Typography>
        </CardContent></Card>
      ) : (
        <Grid container spacing={2}>
          {locationGroups.map(group => (
            <Grid item xs={12} sm={6} md={4} key={group.tableNum}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" color="primary">Group #{group.tableNum}</Typography>
                    <Box>
                      <IconButton size="small" color="primary" onClick={() => handleOpenEdit(group)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(group.tableNum)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Referenced as: <code>U:{group.tableNum}</code> | {group.locations.length} locations
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {group.locations.map(loc => (
                      <Chip key={loc} label={loc} size="small" variant="outlined" />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingGroup ? `Edit Location Group #${editingGroup.tableNum}` : 'Add Location Group'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {!editingGroup && (
            <TextField fullWidth label="Table Number" type="number" value={tableNum}
              onChange={(e) => setTableNum(e.target.value)} sx={{ mb: 2, mt: 1 }}
              helperText="Unique ID — referenced as U:N in rules" inputProps={{ min: 1 }} />
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Locations ({locations.length}) {editingGroup && `— Group #${editingGroup.tableNum}`}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField fullWidth size="small" label="Add Location" value={locationInput}
              onChange={(e) => setLocationInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddLocation(); } }}
              placeholder="e.g. N:US, P:JFK, C:NYC" helperText="P:XXX (airport), C:XXX (city), N:XX (country)" />
            <Button variant="outlined" onClick={handleAddLocation} sx={{ minWidth: 80 }}>Add</Button>
          </Box>

          {locations.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {locations.map(loc => (
                <Chip key={loc} label={loc} onDelete={() => setLocations(locations.filter(l => l !== loc))} />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>{editingGroup ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
